import { useCallback, useEffect, useRef } from 'react'

import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { paymentCancelledEvent } from 'src/Helpers/TagManager'
import { getUserSelector } from 'src/Redux/auth-process/userSlice/selectors'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getCampaignTikTokCampaignToScheduleDetails } from 'src/Redux/schedule-process/tiktokCampaign/api-actions'
import { getTiktokPrice } from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { usePaymentMethod } from 'src/Helpers/usePaymentMethod'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'

import { useResetQueryParams } from '../Helpers/useResetQueryParams'

import { useCustomSearchParams } from './useCustomSearchParams'
interface PaymentCancelledEvents {
  handlePaymentCancelledTikTokEvent: (params: { platformName: string }) => void
}

export const usePaymentCancelledTikTokEvents = (): PaymentCancelledEvents => {
  const dispatch = useAppDispatch()
  const param = useParams()
  const { data } = useMetadataQuery(null)
  const { data: subscription } = useGetSubscriptionQuery()
  const hasPlan = Boolean(
    subscription?.status === 'active' || subscription?.status === 'canceled',
  )
  const campaignId = Number(param.number) || 0
  const user = useSelector(getUserSelector)
  const paymentMethod = useSelector(getPaymentMethod)
  const price = useAppSelector(getTiktokPrice)
  const amountOfCampaigns = getAllCampaignsCount(data)
  const { search } = useCustomSearchParams()
  const paymentCancelled = search.get('payment')
  const priceFromQuery = Number(search.get('price'))
  const isPayPalToken = search.get('token')
  const isPayPalSaved = search.get('isPayPalSaved')

  const initialized = useRef(false)

  const resetQueryParams = useResetQueryParams([
    'payment',
    'token',
    'price',
    'isPayPalSaved',
  ])

  const paymentMethodFromFunction = usePaymentMethod(
    isPayPalToken,
    isPayPalSaved,
    paymentMethod,
  )

  const handlePaymentCancelledTikTokEvent = useCallback(
    async (params: { platformName: string }): Promise<void> => {
      await dispatch(
        getCampaignTikTokCampaignToScheduleDetails({
          tiktokCampaignId: campaignId,
          hasPlan,
        }),
      )
        .unwrap()
        .then((res) => {
          paymentCancelledEvent({
            campaignId,
            paymentMethod: paymentMethodFromFunction,
            genresList: '',
            userId: user.id,
            userRole: user.role,
            campaignNumber: amountOfCampaigns,
            platformName: params.platformName,
            campaignType: res.type,
            campaignValue: price ?? priceFromQuery ?? 0,
            paymentAmount: price ?? priceFromQuery ?? 0,
          })

          resetQueryParams()

          return null
        })
        .catch(() => null)
    },
    [
      dispatch,
      campaignId,
      hasPlan,
      paymentMethodFromFunction,
      user.id,
      user.role,
      amountOfCampaigns,
      price,
      priceFromQuery,
      resetQueryParams,
    ],
  )

  useEffect(() => {
    if (paymentCancelled === 'cancelled' && !initialized.current) {
      initialized.current = true

      void handlePaymentCancelledTikTokEvent({
        platformName: CampaignPlatformTypeEnum.TIKTOK,
      })
    }
  }, [handlePaymentCancelledTikTokEvent, paymentCancelled])

  return {
    handlePaymentCancelledTikTokEvent,
  }
}
